import type { ComissaoRenovacaoDeltaDetalhe } from './comissaoRenovacaoDelta';
import { FATOR_COMISSAO_FORA_SUL } from './foraSul';
export { FATOR_COMISSAO_FORA_SUL };
/**
 * Mapa LEGADO de categorias — mantido apenas como fallback quando nenhum mapa
 * dinâmico (montado via `useProdutosCategorias` a partir da Matriz Produto x KPIs)
 * é fornecido. Use o mapa dinâmico sempre que possível.
 *
 * Inclui nomes atuais (PN NOVO/INCREMENTO, HA NOVO/INCREMENTO, RENOVAÇAO,
 * TT POS-PJ, TT PRE-PJ, TV) e nomes legados (PN PF-PJ, MP MOVEL, TT MÓVEL...)
 * para que dados históricos continuem agregando corretamente caso a query do
 * mapa dinâmico ainda não tenha resolvido.
 */
export const CATEGORIA_PRODUTO_MAP: Record<string, string[]> = {
  'PN': ['PN NOVO', 'PN INCREMENTO', 'PN PF-PJ', 'PN PJ-PJ'],
  'HA': ['HA NOVO', 'HA INCREMENTO', 'HA'],
  'FIBRA': ['FIBRA', 'TV'],
  'TT': ['TT POS-PJ', 'TT MÓVEL'],
  'TTPRE': ['TT PRE-PJ'],
  'MP': ['RENOVAÇAO', 'MP MOVEL'],
  'MIGRACAO': ['RENOVAÇAO', 'TT POS-PJ', 'TT PRE-PJ', 'MP MOVEL/FIXA UP', 'MIGRACAO', 'MIGRAÇÃO'],
};

export function getCategoriaComissao(
  produtoNome: string,
  categoriasMap?: Record<string, string[]>
): string | null {
  const mapa = categoriasMap ?? CATEGORIA_PRODUTO_MAP;
  for (const [categoria, produtos] of Object.entries(mapa)) {
    if (produtos.includes(produtoNome)) {
      return categoria;
    }
  }
  return null;
}

export interface FaturamentoPorCategoria {
  pn: number;
  ha: number;
  fibra: number;
  tt: number;
  ttpre: number;
  mp: number;
  /**
   * (Douglas 28/07) Faturamento de T.I. — soma o subtotal dos produtos marcados
   * com o KPI `produtos.fat_ti`.
   *
   * ENTRA no `total` (Douglas 29/07): "quanto mais eles vendem, mais eles podem
   * subir o faturamento na faixa". T.I. e Avançada sao faturamento como qualquer
   * outro — ajudam a subir de faixa. Sao categorias novas, nao mudanca de regra:
   * antes esses produtos nao entravam em categoria nenhuma e ficavam fora da
   * conta inteira.
   */
  ti: number;
  /** (Douglas 28/07) Faturamento de Fixa Avançada. */
  avancada: number;
  total: number;
  /**
   * (Douglas 26/09/2026) A parte de cada categoria vendida FORA SUL (cliente
   * fora de RS/SC/PR). Já está somada nos campos acima — venda, faixa e meta
   * contam inteiras. Só a produtividade paga metade: ver `baseComissionavel`.
   * Opcional porque foto de mês fechado e objetos montados à mão não têm.
   */
  foraSul?: FaturamentoForaSul;
}

export type FaturamentoForaSul = Omit<FaturamentoPorCategoria, 'foraSul'>;

type CategoriaFaturamento = Exclude<keyof FaturamentoPorCategoria, 'total' | 'foraSul'>;

const faturamentoZerado = (): FaturamentoForaSul => ({ pn: 0, ha: 0, fibra: 0, tt: 0, ttpre: 0, mp: 0, ti: 0, avancada: 0, total: 0 });

/**
 * O que a produtividade multiplica pelo percentual: o faturamento da categoria
 * com a parte fora sul valendo metade. É a ÚNICA porta — todo `fat × %` do
 * cálculo passa por aqui, para a tela, o Excel e o fechamento darem o mesmo.
 */
export function baseComissionavel(
  f: { [K in keyof FaturamentoForaSul]?: number } & { foraSul?: Partial<FaturamentoForaSul> },
): FaturamentoForaSul {
  const fora = f.foraSul ?? {};
  const desconto = 1 - FATOR_COMISSAO_FORA_SUL;
  const base = (k: keyof FaturamentoForaSul) => Number(f[k] ?? 0) - Number(fora[k] ?? 0) * desconto;
  return {
    pn: base('pn'), ha: base('ha'), fibra: base('fibra'), tt: base('tt'), ttpre: base('ttpre'),
    mp: base('mp'), ti: base('ti'), avancada: base('avancada'), total: base('total'),
  };
}

export interface QuantidadesMetas {
  altas: number;
  fibras: number;
  tt: number;
  mp: number;
  migracao: number;
  /** (Douglas 29/08) Serviços Digitais — licenças (qtd_sd), família TI. */
  sd: number;
  /** (Douglas 21/10/2026) TV CPF — quantidade (qtd_tv). Meta só do CPF; não é fibra. */
  tv?: number;
}

export interface ResultadoComissao {
  faturamentos: FaturamentoPorCategoria;
  quantidades: QuantidadesMetas;
  /** Quantidades incluindo DIRETORIA — para exibição nos cards de meta */
  quantidadesMeta: QuantidadesMetas;
  /** Faturamentos incluindo DIRETORIA — para exibição nos cards de meta */
  faturamentosMeta: FaturamentoPorCategoria;
  faixaEnquadrada: number;
  percentuais: {
    pn: number;
    ha: number;
    fibra: number;
    tt: number;
    ttpre: number;
    mp: number;
    /** (Douglas 28/07) % de T.I. — `commission_rules.ti_pct`. 0 = não paga. */
    ti: number;
    /** (Douglas 28/07) % de Fixa Avançada — `commission_rules.avancada_pct`. */
    avancada: number;
  };
  comissoes: {
    pn: number;
    ha: number;
    fibra: number;
    tt: number;
    ttpre: number;
    mp: number;
    ti: number;
    avancada: number;
    total: number;
  };
  metas: {
    altas: number;
    fibras: number;
    fat_vn: number;
    fat_total: number;
    qtd_tt: number;
    qtd_mp: number;
    qtd_migracao: number;
    qtd_sd: number;
    qtd_tv?: number;
    fat_ti: number;
    fat_avancada: number;
    usar_altas: boolean;
    usar_fibras: boolean;
    usar_fat_vn: boolean;
    usar_fat_total: boolean;
    usar_qtd_tt: boolean;
    usar_qtd_mp: boolean;
    usar_qtd_migracao: boolean;
    usar_qtd_sd: boolean;
    usar_qtd_tv?: boolean;
    usar_fat_ti: boolean;
    usar_fat_avancada: boolean;
    /** (Douglas 26/10/2026) Renovação abaixo da meta, validada pelas fibras a mais. */
    renovacao_por_fibra?: boolean;
    /** (Douglas 29/10/2026) Regra 3 ligada: TT + Renovação juntos (Migração). */
    usar_migracao_junta?: boolean;
    /** Meta da Migração = meta de TT + meta de Renovação (no gestor, × N). */
    migracao_junta?: number;
    /** TT + Renovação que contam na Migração (no gestor, sem a Diretoria). */
    migracao_junta_realizado?: number;
    /** % que a regra 3 paga (50). */
    migracao_junta_pct?: number;
    /** A meta foi batida pela regra 3: a comissão já vem com o %. */
    por_migracao?: boolean;
  };
  bateuMeta: boolean;
  proximaFaixa: number | null;
  faltaParaProximaFaixa: number;
  nVendedores?: number;
  /**
   * Quando true, indica que a comissão foi calculada pela regra mínima de
   * período de experiência (consultor com `contratos_experiencia` ativo no mês).
   * A UI usa essa flag para trocar mensagens de "meta bloqueada" pelo banner
   * de "Período de experiência — comissão liberada".
   */
  regraExperiencia?: boolean;
  renovacaoDelta: ComissaoRenovacaoDeltaDetalhe;
}

export interface CategoriasComissaoConfig {
  /** Mapa dinâmico categoria → lista de nomes de produto (preferencial) */
  categoriasMap?: Record<string, string[]>;
  /** Produtos que contam em "altas" (qtd_altas=true) */
  produtosAltaNomes?: string[];
  /** Produtos que contam em "fibras" (qtd_fibra=true). Se omitido: ['FIBRA']. */
  produtosFibraNomes?: string[];
  /** Produtos que contam em "tt" (TT POS-PJ + TT PRE-PJ). Se omitido: defaults legados. */
  produtosTtNomes?: string[];
  /** Produtos que contam em "mp" (qtd_mp_movel=true). Se omitido: defaults legados. */
  produtosMpNomes?: string[];
  /** Produtos que contam em "migração" (qtd_mp_movel_fixa_up=true) */
  produtosMigracaoNomes?: string[];
  /** Produtos que contam em "SD" (qtd_sd=true — licenças). Se omitido: nenhum. */
  produtosSdNomes?: string[];
  /** Produtos que contam em "TV" (qtd_tv=true). Se omitido: nenhum. */
  produtosTvNomes?: string[];
  /** Produtos que contam em "T.I." (fat_ti=true). Se omitido: nenhum. */
  produtosTiNomes?: string[];
  /** Produtos que contam em "Fixa Avançada" (fat_avancada=true). Se omitido: nenhum. */
  produtosAvancadaNomes?: string[];
}

export function calcularFaturamentoPorCategoria(
  itens: Array<{
    produto: string;
    quantidade: number;
    subtotal: number;
    fora_sul?: boolean | null;
  }>,
  /**
   * Configuração das categorias. Pode receber:
   *  - Um objeto `CategoriasComissaoConfig` (modo novo, recomendado)
   *  - Um `string[]` legado (lista de produtosAlta) — para retrocompatibilidade
   */
  configOrAltas?: CategoriasComissaoConfig | string[],
  /** [LEGADO] produtosMigracaoNomes — usar `CategoriasComissaoConfig.produtosMigracaoNomes` */
  produtosMigracaoLegado?: string[]
): { faturamentos: FaturamentoPorCategoria; quantidades: QuantidadesMetas } {
  const foraSul = faturamentoZerado();
  const faturamentos: FaturamentoPorCategoria = { pn: 0, ha: 0, fibra: 0, tt: 0, ttpre: 0, mp: 0, ti: 0, avancada: 0, total: 0, foraSul };
  const quantidades: QuantidadesMetas = { altas: 0, fibras: 0, tt: 0, mp: 0, migracao: 0, sd: 0, tv: 0 };
  const somar = (key: CategoriaFaturamento, item: { subtotal: number; fora_sul?: boolean | null }) => {
    const valor = Number(item.subtotal || 0);
    faturamentos[key] += valor;
    faturamentos.total += valor;
    if (item.fora_sul === true) {
      foraSul[key] += valor;
      foraSul.total += valor;
    }
  };

  // Normalizar config (aceita assinatura antiga: arrays soltos)
  const config: CategoriasComissaoConfig = Array.isArray(configOrAltas) || configOrAltas === undefined
    ? {
        produtosAltaNomes: configOrAltas as string[] | undefined,
        produtosMigracaoNomes: produtosMigracaoLegado,
      }
    : configOrAltas;

  const categoriasMap = config.categoriasMap;

  const altasProdutos = config.produtosAltaNomes && config.produtosAltaNomes.length > 0
    ? config.produtosAltaNomes
    : ['PN NOVO', 'PN INCREMENTO', 'HA NOVO', 'HA INCREMENTO', 'PEN/BOX/TABLET', 'PN PF-PJ', 'PN PJ-PJ', 'HA'];

  const fibraProdutos = config.produtosFibraNomes && config.produtosFibraNomes.length > 0
    ? config.produtosFibraNomes
    : ['FIBRA'];

  const ttProdutos = config.produtosTtNomes && config.produtosTtNomes.length > 0
    ? config.produtosTtNomes
    : ['TT POS-PJ', 'TT PRE-PJ', 'TT MÓVEL'];

  const mpProdutos = config.produtosMpNomes && config.produtosMpNomes.length > 0
    ? config.produtosMpNomes
    : ['RENOVAÇAO', 'MP MOVEL'];

  const migracaoProdutos = config.produtosMigracaoNomes && config.produtosMigracaoNomes.length > 0
    ? config.produtosMigracaoNomes
    : ['RENOVAÇAO', 'TT POS-PJ', 'TT PRE-PJ', 'MP MOVEL/FIXA UP', 'TT MÓVEL'];

  // (Douglas 28/07) T.I. não tem default legado: sem a lista vinda de
  // `produtos.fat_ti` o faturamento fica 0 e a meta simplesmente não é atingida.
  // É o comportamento seguro — melhor não bater do que inventar uma lista fixa
  // que envelhece e passa a pagar (ou travar) por produto errado.
  const tiProdutos = config.produtosTiNomes ?? [];
  const avancadaProdutos = config.produtosAvancadaNomes ?? [];
  // (Douglas 29/08) SD segue a mesma regra do T.I.: sem lista da Matriz, conta 0.
  const sdProdutos = config.produtosSdNomes ?? [];
  // (Douglas 21/10/2026) TV CPF: quantidade própria, fora das fibras; o
  // faturamento dela segue como fibra pela Matriz (fat_fibra).
  const tvProdutos = config.produtosTvNomes ?? [];

  for (const item of itens) {
    const categoria = getCategoriaComissao(item.produto, categoriasMap);

    if (categoria && categoria !== 'MIGRACAO') {
      somar(categoria.toLowerCase() as CategoriaFaturamento, item);
    }

    if (altasProdutos.includes(item.produto)) {
      quantidades.altas += item.quantidade || 0;
    }
    if (fibraProdutos.includes(item.produto)) {
      quantidades.fibras += item.quantidade || 0;
    }
    if (tiProdutos.includes(item.produto)) {
      somar('ti', item);
    }
    if (avancadaProdutos.includes(item.produto)) {
      somar('avancada', item);
    }
    if (ttProdutos.includes(item.produto)) {
      quantidades.tt += item.quantidade || 0;
    }
    if (mpProdutos.includes(item.produto)) {
      quantidades.mp += item.quantidade || 0;
    }
    if (migracaoProdutos.includes(item.produto)) {
      quantidades.migracao += item.quantidade || 0;
    }
    if (sdProdutos.includes(item.produto)) {
      quantidades.sd += item.quantidade || 0;
    }
    if (tvProdutos.includes(item.produto)) {
      quantidades.tv = (quantidades.tv ?? 0) + (item.quantidade || 0);
    }
  }

  return { faturamentos, quantidades };
}

// Hook principal para cálculo de comissão
// usuarioId opcional: quando fornecido (supervisores individuais), usa regras específicas do usuário
/** Meta-alvo agregada do gestor: soma das metas das equipes (já multiplicada por equipe). */
